import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Download, Send, CheckCircle2, XCircle, Mail, X, Share2, Copy, Eye, Trash2, Link as LinkIcon, Receipt, Repeat } from "lucide-react";
import { PageHeader, Badge, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);

export default function QuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useApp();
  const [q, setQ] = useState(null);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | 'ok' | err string
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const token = localStorage.getItem("progere_token");

  const load = () => axios.get(`${API}/quotes/${id}`).then(({ data }) => {
    setQ(data);
    setEmailTo(data.sent_to || "");
  });
  useEffect(() => { load(); }, [id]);

  const loadShare = async () => {
    const { data } = await axios.get(`${API}/quotes/${id}/share`);
    setShare(data && !data.exists && data.token ? data : null);
  };

  const openShareModal = async () => {
    setShareOpen(true);
    await loadShare();
  };

  const createShare = async () => {
    setShareLoading(true);
    try {
      const { data } = await axios.post(`${API}/quotes/${id}/share`);
      setShare(data);
    } finally { setShareLoading(false); }
  };

  const revokeShare = async () => {
    if (!window.confirm(lang === "fr" ? "Révoquer ce lien ? Le client ne pourra plus l'utiliser." : "Revoke this link? Client won't be able to use it.")) return;
    setShareLoading(true);
    try {
      await axios.delete(`${API}/quotes/${id}/share`);
      setShare(null);
    } finally { setShareLoading(false); }
  };

  const shareUrl = share?.token ? `${window.location.origin}/quote/share/${share.token}` : "";
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const convertToInvoice = async () => {
    try {
      const { data } = await axios.post(`${API}/invoices/from-quote/${id}`);
      nav(`/app/invoices/${data.id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  };

  const remakeQuote = () => {
    // Clone the current quote into a localStorage draft → opens in QuoteBuilder pre-filled
    const draft = {
      client_id: q.client_id || "",
      client_name: q.client_name || "",
      project_id: q.project_id || "",
      project_name: `${q.project_name} (${lang === "fr" ? "copie" : "copy"})`,
      project_address: q.project_address || "",
      work_type: q.work_type || "",
      work_description: q.work_description || "",
      scope_of_work: q.scope_of_work || "",
      exclusions: q.exclusions || "",
      conditions: q.conditions || "",
      payment_terms: q.payment_terms || "Net 30 jours",
      items: (q.items || []).map((it) => ({ ...it })),
      language: q.language || lang,
    };
    try { localStorage.setItem("progere_draft_quote_v1", JSON.stringify(draft)); } catch {/* ignore */}
    nav("/app/quotes/new");
  };

  const setStatus = async (s) => {
    await axios.patch(`${API}/quotes/${id}/status`, { status: s });
    load();
  };

  const downloadPdf = async () => {
    const url = `${API}/quotes/${id}/pdf${includePhotos ? "?include_photos=true" : ""}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl; a.download = `${q.quote_number}.pdf`; a.click();
    URL.revokeObjectURL(objUrl);
  };

  const sendEmail = async (e) => {
    e?.preventDefault();
    setSending(true); setEmailStatus(null);
    try {
      await axios.post(`${API}/quotes/${id}/email`, {
        to_email: emailTo,
        message: emailMsg,
        include_photos: includePhotos,
      });
      setEmailStatus("ok");
      setTimeout(() => { setEmailOpen(false); setEmailStatus(null); load(); }, 1800);
    } catch (e) {
      setEmailStatus(e?.response?.data?.detail || (lang === "fr" ? "Échec de l'envoi" : "Send failed"));
    } finally {
      setSending(false);
    }
  };

  if (!q) return <div className="flex items-center justify-center h-96"><Spinner /></div>;

  return (
    <div>
      <PageHeader title={`${q.quote_number}`} subtitle={`${q.client_name} · ${q.project_name}`}
        action={
          <div className="flex gap-2 flex-wrap items-center">
            <button className="btn-ghost text-sm" onClick={() => setStatus("sent")} data-testid="status-sent"><Send size={14} /> {t.detail.send}</button>
            <button className="btn-ghost text-sm" onClick={() => setStatus("approved")} data-testid="status-approved"><CheckCircle2 size={14} /> {t.detail.approve}</button>
            <button className="btn-ghost text-sm" onClick={() => setStatus("rejected")} data-testid="status-rejected"><XCircle size={14} /> {t.detail.reject}</button>
            <button className="btn-ghost text-sm" onClick={() => setEmailOpen(true)} data-testid="email-quote-btn"><Mail size={14} /> {lang === "fr" ? "Envoyer au client" : "Email to client"}</button>
            <button className="btn-ghost text-sm" onClick={openShareModal} data-testid="share-quote-btn"><Share2 size={14} /> {lang === "fr" ? "Lien de partage" : "Share link"}</button>
            <button className="btn-ghost text-sm" onClick={remakeQuote} data-testid="remake-quote-btn">
              <Repeat size={14} /> {lang === "fr" ? "Refaire" : "Remake"}
            </button>
            {q.status === "approved" && (
              <button className="btn-red text-sm" onClick={convertToInvoice} data-testid="convert-to-invoice-btn">
                <Receipt size={14} /> {lang === "fr" ? "Convertir en facture" : "Convert to invoice"}
              </button>
            )}
            {q.photos && q.photos.length > 0 && (
              <label className="flex items-center gap-2 px-3 py-2 rounded-full cursor-pointer text-xs"
                style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)", color: "#F5F5F5" }}
                data-testid="toggle-include-photos">
                <input type="checkbox" checked={includePhotos} onChange={(e) => setIncludePhotos(e.target.checked)}
                  className="accent-[#D4AF37]" />
                {lang === "fr" ? `Inclure les ${q.photos.length} photo(s)` : `Include ${q.photos.length} photo(s)`}
              </label>
            )}
            <button className="btn-red text-sm" onClick={downloadPdf} data-testid="download-pdf"><Download size={14} /> {t.detail.pdf}</button>
          </div>
        } />

      <div className="flex items-center gap-4 mb-6">
        <Badge status={q.status} label={t.status[q.status] || q.status} />
        <span className="text-xs" style={{ color: "rgba(245,245,245,0.5)" }}>
          {(q.date || "").slice(0, 10)} → {(q.expiry_date || "").slice(0, 10)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {[
          ["from", "Émetteur", `${q.client_name}`],
          ["addr", t.projects_p.address, q.project_address || "—"],
          ["work", "Type", q.work_type || "—"],
        ].map(([k, label, val]) => (
          <div key={k} className="glass-card p-5">
            <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>{label}</div>
            <div className="text-sm" style={{ color: "#F5F5F5" }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Items */}
      <div className="glass-card p-6 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-4" style={{ color: "#F5F5F5" }}>{t.quotes.builder.items}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uppercase text-xs tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>
                <th className="py-2">#</th><th>{t.quotes.builder.item_desc}</th><th>{t.quotes.builder.qty}</th><th>{t.quotes.builder.unit}</th><th className="text-right">{t.quotes.builder.total_col}</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map((it) => (
                <tr key={it.id} className="border-t" style={{ borderColor: "rgba(245,245,245,0.05)" }}>
                  <td className="py-3" style={{ color: "#D4AF37" }}>{it.item_number}</td>
                  <td>{it.description}</td>
                  <td>{it.quantity}</td>
                  <td>{it.unit}</td>
                  <td className="text-right font-semibold">{fmt(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 ml-auto max-w-md text-sm space-y-2">
          <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}><span>{t.detail.subtotal}</span><span>{fmt(q.subtotal)}</span></div>
          <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}><span>{t.detail.gst}</span><span>{fmt(q.gst)}</span></div>
          <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}><span>{t.detail.qst}</span><span>{fmt(q.qst)}</span></div>
          <div className="flex justify-between pt-3 border-t font-bold text-lg" style={{ borderColor: "rgba(212,175,55,0.3)", color: "#D4AF37" }}><span>{t.detail.total}</span><span>{fmt(q.total)}</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[
          ["scope", t.detail.scope, q.scope_of_work],
          ["excl", t.detail.excl, q.exclusions],
          ["cond", t.detail.cond, q.conditions],
        ].map(([k, label, val]) => (
          <div key={k} className="glass-card p-6">
            <h3 className="font-heading text-sm font-semibold mb-3" style={{ color: "#D4AF37" }}>{label}</h3>
            <p className="text-sm whitespace-pre-line" style={{ color: "rgba(245,245,245,0.78)", lineHeight: 1.6 }}>{val || "—"}</p>
          </div>
        ))}
      </div>

      <div className="glass-card p-6 mt-6">
        <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>{t.detail.payment}</div>
        <div className="text-sm" style={{ color: "#F5F5F5" }}>{q.payment_terms}</div>
        <div className="mt-8 pt-8 border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
          <div className="text-xs uppercase tracking-wider mb-3" style={{ color: "rgba(245,245,245,0.5)" }}>{t.detail.sig}</div>
          <div className="border-b pb-12" style={{ borderColor: "rgba(245,245,245,0.2)" }}>____________________________</div>
        </div>
      </div>

      {q.photos && q.photos.length > 0 && (
        <div className="glass-card p-6 mt-6">
          <h3 className="font-heading text-sm font-semibold mb-4" style={{ color: "#D4AF37" }}>
            {(lang === "fr" ? "Photos et plans du chantier" : "Site photos & plans")} ({q.photos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {q.photos.map((p, i) => (
              <a key={i} href={`data:image/jpeg;base64,${p}`} target="_blank" rel="noreferrer"
                 className="block aspect-square rounded-2xl overflow-hidden hover:opacity-90 transition"
                 style={{ border: "1px solid rgba(212,175,55,0.25)" }}
                 data-testid={`saved-photo-${i}`}>
                <img src={`data:image/jpeg;base64,${p}`} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Email-to-client modal */}
      {emailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }} onClick={() => !sending && setEmailOpen(false)} data-testid="email-modal">
          <div className="glass-strong w-full max-w-lg p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/5" onClick={() => !sending && setEmailOpen(false)} data-testid="email-modal-close">
              <X size={18} color="rgba(245,245,245,0.7)" />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
                <Mail size={18} color="#D4AF37" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>{lang === "fr" ? "Envoyer la soumission" : "Send quote"}</h3>
                <p className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>{q.quote_number} · {q.client_name}</p>
              </div>
            </div>
            <form onSubmit={sendEmail} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Courriel du destinataire" : "Recipient email"}</label>
                <input type="email" required className="input-dark mt-2" placeholder="client@exemple.ca"
                  value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-modal-to" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Message (optionnel)" : "Message (optional)"}</label>
                <textarea className="input-dark mt-2" rows={4} placeholder={lang === "fr" ? "Ajoutez un message personnel..." : "Add a personal message..."}
                  value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} data-testid="email-modal-message" />
              </div>
              {q.photos && q.photos.length > 0 && (
                <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "rgba(245,245,245,0.7)" }}>
                  <input type="checkbox" checked={includePhotos} onChange={(e) => setIncludePhotos(e.target.checked)} className="accent-[#D4AF37]" />
                  {lang === "fr" ? `Inclure les ${q.photos.length} photo(s) dans le PDF` : `Include ${q.photos.length} photo(s) in the PDF`}
                </label>
              )}
              {emailStatus === "ok" && (
                <div className="text-sm px-3 py-2 rounded-xl flex items-center gap-2" style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }} data-testid="email-modal-ok">
                  <CheckCircle2 size={14} /> {lang === "fr" ? "Courriel envoyé avec succès !" : "Email sent successfully!"}
                </div>
              )}
              {emailStatus && emailStatus !== "ok" && (
                <div className="text-sm px-3 py-2 rounded-xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }} data-testid="email-modal-err">
                  {emailStatus}
                </div>
              )}
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" className="btn-ghost text-sm" onClick={() => setEmailOpen(false)} disabled={sending} data-testid="email-modal-cancel">
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button type="submit" className="btn-red text-sm" disabled={sending} data-testid="email-modal-send">
                  {sending ? <Spinner /> : <><Send size={14} /> {lang === "fr" ? "Envoyer" : "Send"}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Share-link modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }} onClick={() => setShareOpen(false)} data-testid="share-modal">
          <div className="glass-strong w-full max-w-lg p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/5" onClick={() => setShareOpen(false)} data-testid="share-modal-close">
              <X size={18} color="rgba(245,245,245,0.7)" />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
                <Share2 size={18} color="#D4AF37" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>{lang === "fr" ? "Partager avec le client" : "Share with client"}</h3>
                <p className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>{lang === "fr" ? "Le client peut consulter et signer sans créer de compte." : "Client can view and sign without an account."}</p>
              </div>
            </div>

            {!share ? (
              <div className="text-center py-6">
                <LinkIcon size={36} color="rgba(245,245,245,0.4)" className="mx-auto mb-4" />
                <p className="text-sm mb-5" style={{ color: "rgba(245,245,245,0.7)" }}>
                  {lang === "fr" ? "Générez un lien public sécurisé pour partager cette soumission." : "Generate a secure public link to share this quote."}
                </p>
                <button onClick={createShare} className="btn-red" disabled={shareLoading} data-testid="share-generate">
                  {shareLoading ? <Spinner /> : <><Share2 size={14} /> {lang === "fr" ? "Générer un lien" : "Generate link"}</>}
                </button>
              </div>
            ) : (
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Lien à partager" : "Share URL"}</label>
                <div className="flex gap-2 mt-2">
                  <input className="input-dark text-xs flex-1" readOnly value={shareUrl} data-testid="share-url" />
                  <button onClick={copyShareUrl} className="btn-red text-sm" data-testid="share-copy">
                    {copied ? <><CheckCircle2 size={14} /> {lang === "fr" ? "Copié" : "Copied"}</> : <><Copy size={14} /> {lang === "fr" ? "Copier" : "Copy"}</>}
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-5 text-center">
                  <div className="p-3 rounded-xl" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.15)" }}>
                    <Eye size={16} color="#D4AF37" className="mx-auto mb-1" />
                    <div className="text-2xl font-heading font-bold" style={{ color: "#F5F5F5" }}>{share.views || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Vues" : "Views"}</div>
                  </div>
                  <div className="p-3 rounded-xl" style={{
                    background: share.status === "accepted" ? "rgba(74,222,128,0.08)" : share.status === "rejected" ? "rgba(192,0,0,0.08)" : "rgba(245,245,245,0.04)",
                    border: `1px solid ${share.status === "accepted" ? "rgba(74,222,128,0.25)" : share.status === "rejected" ? "rgba(192,0,0,0.3)" : "rgba(245,245,245,0.08)"}`,
                  }}>
                    {share.status === "accepted" ? <CheckCircle2 size={16} color="#4ADE80" className="mx-auto mb-1" /> :
                     share.status === "rejected" ? <XCircle size={16} color="#C00000" className="mx-auto mb-1" /> :
                     <LinkIcon size={16} color="rgba(245,245,245,0.5)" className="mx-auto mb-1" />}
                    <div className="text-xs font-heading font-bold" style={{ color: "#F5F5F5" }}>
                      {share.status === "accepted" ? (lang === "fr" ? "Acceptée" : "Accepted") :
                       share.status === "rejected" ? (lang === "fr" ? "Refusée" : "Rejected") :
                       (lang === "fr" ? "En attente" : "Pending")}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Statut" : "Status"}</div>
                  </div>
                  <div className="p-3 rounded-xl" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.15)" }}>
                    <Mail size={16} color="#D4AF37" className="mx-auto mb-1" />
                    <div className="text-xs font-heading font-bold truncate" style={{ color: "#F5F5F5" }}>{share.signer_name || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Signé par" : "Signed by"}</div>
                  </div>
                </div>

                {share.signer_note && (
                  <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: "rgba(212,175,55,0.05)", borderLeft: "2px solid #D4AF37", color: "rgba(245,245,245,0.75)" }}>
                    {share.signer_note}
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={revokeShare} className="btn-ghost text-xs" disabled={shareLoading} data-testid="share-revoke">
                    <Trash2 size={12} /> {lang === "fr" ? "Révoquer le lien" : "Revoke link"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
